import Actions from 'reducers/PlayList'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { PlayListService } from 'services'
import { storeHelpers } from 'utils'
import { PlaylistSort } from 'constants/enums'
import { maxPagesize } from 'config'
import type { AppDispatch } from 'types/store'

interface ListOptions {
  page: number
  pagesize?: number
  query?: PlaylistListQuery
  searchToken?: string
  sort?: string
  summary?: boolean
}

interface PlaylistListQuery {
  searchToken: string
  sort: string
}

let listRequestSequence = 0
let playlistRemovalSequence = 0

const createListRequestId = () => {
  listRequestSequence += 1
  return `playlist-list-${Date.now()}-${listRequestSequence}`
}

const getCurrentListQuery = (playList: any): PlaylistListQuery => ({
  searchToken:
    typeof playList?.searchToken === 'string' ? playList.searchToken : '',
  sort:
    typeof playList?.sort === 'string'
      ? playList.sort
      : PlaylistSort.newest.value,
})

const getAppliedListQuery = (playList: any): PlaylistListQuery => {
  const currentQuery = getCurrentListQuery(playList)

  if (!playList?.listQuery) {
    return currentQuery
  }

  return {
    searchToken:
      typeof playList.listQuery.searchToken === 'string'
        ? playList.listQuery.searchToken
        : currentQuery.searchToken,
    sort:
      typeof playList.listQuery.sort === 'string'
        ? playList.listQuery.sort
        : currentQuery.sort,
  }
}

export const list =
  ({ page, pagesize, query: requestedQuery, summary }: ListOptions) =>
  async (
    dispatch: AppDispatch,
    getState?: () => { playList: any }
  ) => {
    const requestId = createListRequestId()
    let requestStarted = false

    try {
      const { playList }: any = getState
        ? getState()
        : await storeHelpers.asyncGetStoreState()
      const query =
        requestedQuery ||
        (page === 1
          ? getCurrentListQuery(playList)
          : getAppliedListQuery(playList))

      dispatch(Actions.listRequestStarted({ page, query, requestId }))
      requestStarted = true

      const response: any = await PlayListService.list(
        page,
        pagesize,
        query.searchToken,
        query.sort,
        summary
      )
      const items = response.data.items
      const totalItemsCount = response.data.total

      dispatch(
        Actions.loadingPlaylistSuccess(
          items,
          page,
          totalItemsCount,
          requestId
        )
      )
      return response.data
    } catch (error) {
      console.warn(error)
      dispatch(
        Actions.loadingPlaylistFail(
          error,
          requestStarted ? requestId : undefined
        )
      )
      return null
    }
  }

export const listAll = () => async (dispatch: AppDispatch) => {
  const removalSequence = playlistRemovalSequence

  try {
    const response: any = await PlayListService.list(
      1,
      maxPagesize,
      undefined,
      PlaylistSort.newest.value,
      true
    )
    const items = response.data.items

    // An older selector fetch must not restore a successfully removed playlist.
    if (removalSequence !== playlistRemovalSequence) {
      return null
    }

    dispatch(Actions.setPlaylistAbstracts(items))
    return response.data
  } catch (error) {
    console.warn(error)
    return null
  }
}

export const create =
  (playList: any, successCallBack?: (data: any) => void) =>
  async (dispatch: AppDispatch) => {
    try {
      const response: any = await PlayListService.create(playList)
      const createdPlaylist = { ...playList, ...response.data }

      dispatch(Actions.createPlaylistSuccess(createdPlaylist))
      dispatch(list({ page: 1 }) as any)
      dispatch(listAll() as any)
      if (successCallBack) {
        successCallBack(response.data)
      }

      return response.data
    } catch (error) {
      console.warn(error)
      throw error
    }
  }

export const refreshPlaylist =
  (id: string, successCallBack?: (playlist: any) => void) =>
  (dispatch: AppDispatch) => {
    PlayListService.getPlaylist(id)
      .then((response: any) => {
        const playlist = response.data
        PlayListService.getTracklist(id).then((response2: any) => {
          playlist.songs = response2.data.items
          dispatch(Actions.updatePlaylist(playlist))
          if (successCallBack) {
            successCallBack(playlist)
          }
        })
      })
      .catch((e: any) => console.error(e))
  }

export const updatePlaylist =
  (playlist: any, successBlock: (() => void) | null = null) =>
  async (dispatch: AppDispatch) => {
    try {
      await PlayListService.updateItem(playlist)
      dispatch(Actions.updatePlaylist(playlist))
      dispatch(refreshPlaylist(playlist.playlistid) as any)
      dispatch(PlayQueueThunks.updatePlaylist(playlist))
      if (successBlock) {
        successBlock()
      }

      return playlist
    } catch (error) {
      console.warn(error)
      throw error
    }
  }

export const deletePlaylist =
  (playlist: any, onSuccess?: () => void) =>
  async (
    dispatch: AppDispatch,
    getState?: () => { playList: any }
  ) => {
    dispatch(Actions.deletePlaylistStart())

    try {
      await PlayListService.deleteItem(playlist.playlistid)
    } catch (error) {
      console.warn(error)
      dispatch(Actions.deletePlaylistError())
      throw error
    }

    const query = getState
      ? getAppliedListQuery(getState().playList)
      : undefined

    playlistRemovalSequence += 1
    dispatch(Actions.deletePlaylistSuccess(playlist))
    dispatch(PlayQueueThunks.deletePlaylist(playlist))
    // Repair offset pagination independently: a successful deletion must not
    // become retryable just because refreshing the library fails.
    dispatch(list({ page: 1, query }) as any)

    if (typeof onSuccess === 'function') {
      onSuccess()
    }

    return playlist
  }

export const deleteSong =
  (playlist: any, song: any) => (dispatch: AppDispatch) => {
    dispatch(Actions.updateLoadingPlaylist(true))
    PlayListService.deleteTrack(playlist, song)
      .then(() => {
        dispatch(refreshPlaylist(playlist.playlistid) as any)
        dispatch(Actions.updateLoadingPlaylist(false))
      })
      .catch(() => {
        dispatch(Actions.updateLoadingPlaylist(false))
      })
  }

export const deleteAllSongs = (playlist: any) => (dispatch: AppDispatch) => {
  dispatch(Actions.updateLoadingPlaylist(true))
  PlayListService.deleteAllTrack(playlist)
    .then(() => {
      dispatch(
        refreshPlaylist(playlist.playlistid, () => {
          dispatch(Actions.updateLoadingPlaylist(false))
        }) as any
      )
    })
    .catch(() => {
      dispatch(Actions.updateLoadingPlaylist(false))
    })
}

export const addTracks =
  (
    id: string,
    tracks: any[],
    successBlock: ((data: any) => void) | null = null,
    rejectOnError = false
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions.addTracksStart())

    try {
      const response: any = await PlayListService.addTracks(id, tracks)
      if (successBlock) {
        successBlock(response.data)
      }
      dispatch(refreshPlaylist(id) as any)
      dispatch(Actions.addTracksSuccess())
      return response.data
    } catch (error) {
      console.warn(error)
      dispatch(Actions.addTracksFail())
      if (rejectOnError) {
        throw error
      }

      return null
    }
  }

export const pin = (playlist: any) => (dispatch: AppDispatch) => {
  PlayListService.pin(playlist)
    .then(() =>
      dispatch(Actions.pinPlaylist(Object.assign({}, playlist, { pinned: true })))
    )
    .catch()
}

export const unpin = (playlist: any) => (dispatch: AppDispatch) => {
  PlayListService.unpin(playlist)
    .then(() =>
      dispatch(Actions.unpinPlaylist(Object.assign({}, playlist, { pinned: false })))
    )
    .catch()
}

export default {
  list,
  listAll,
  create,
  deleteSong,
  refreshPlaylist,
  deleteAllSongs,
  addTracks,
  updatePlaylist,
  deletePlaylist,
  pin,
  unpin,
}
